/**
 * Approach cards shown inline in the chat flow.
 * Stacked vertically with icon on the left (horizontal layout within each card).
 * Spring-based staggered entrance with hover/tap micro-interactions.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Lightbulb, MessageSquare } from 'lucide-react';
import { cardContainerVariants, chipVariants } from './animations';

interface Props {
  onSelect: (intent: 'score_driven' | 'targeted_revision') => void;
  onDirectRun?: () => void;
  disabled?: boolean;
}

const ApproachSelector: React.FC<Props> = ({ onSelect, onDirectRun, disabled }) => {

  return (
    <motion.div
      variants={cardContainerVariants}
      initial="initial"
      animate="animate"
      className="flex flex-col gap-3 w-full"
    >
      <motion.button
        variants={chipVariants}
        whileHover={{ scale: 1.015 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => !disabled && onSelect('score_driven')}
        disabled={disabled}
        className="group flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 text-left transition-colors hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:pointer-events-none"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-all duration-200 group-hover:bg-primary/10 group-hover:text-primary">
          <Lightbulb className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-heading text-sm">Review Insights</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Hobbes has reviewed your recent conversations and spotted areas to improve.
          </p>
        </div>
      </motion.button>

      <motion.button
        variants={chipVariants}
        whileHover={{ scale: 1.015 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => !disabled && onSelect('targeted_revision')}
        disabled={disabled}
        className="group flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 text-left transition-colors hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:pointer-events-none"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-all duration-200 group-hover:bg-primary/10 group-hover:text-primary">
          <MessageSquare className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-heading text-sm">Share Your Feedback</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Describe what's not working. I'll ask a few questions and build a plan.
          </p>
        </div>
      </motion.button>

      {onDirectRun && (
        <motion.button
          variants={chipVariants}
          whileHover={{ scale: 1.015 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => !disabled && onDirectRun()}
          disabled={disabled}
          className="group flex items-center gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/10 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-all duration-200 group-hover:bg-primary/15">
            <FlaskConical className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-heading text-sm">Simulate & Score</h3>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Run simulated buyer conversations and compare performance scores.
            </p>
          </div>
        </motion.button>
      )}
    </motion.div>
  );
};

export default ApproachSelector;
